import { useEffect, useState } from "react";
import { BellRing, BellOff, Send, Loader2, Smartphone, CheckCircle2, XCircle, AlertCircle, Zap } from "lucide-react";
import { getSubscription, subscribeToPush, unsubscribeFromPush, isPushSupported, isIOSStandalone } from "../../lib/push";
import { API } from "../../lib/api";

function StatusRow({ ok, label, detail }) {
  return (
    <div className={`push-diag-row ${ok ? "ok" : "warn"}`}>
      {ok ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
      <div>
        <b>{label}</b>
        {detail && <small>{detail}</small>}
      </div>
    </div>
  );
}

export default function PushSetup({ adminPin }) {
  const [supported] = useState(isPushSupported());
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [testBody, setTestBody] = useState("Test bildirimi — bu mesajı gördüyseniz push sistemi çalışıyor.");
  const [count, setCount] = useState(0);
  const [ios] = useState(/iPad|iPhone|iPod/.test(navigator.userAgent));
  const [standalone] = useState(isIOSStandalone());
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "unavailable");
  const [swReady, setSwReady] = useState(false);

  const refresh = async () => {
    if (supported) {
      try {
        const s = await getSubscription();
        setSubscribed(!!s);
        const r = await fetch(`${API}/push/subscriptions`).then((x) => x.json());
        setCount(r.count || 0);
      } catch {}
    }
    try {
      const reg = await navigator.serviceWorker?.getRegistration?.("/");
      setSwReady(!!reg);
    } catch {}
    if (typeof Notification !== "undefined") setPermission(Notification.permission);
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [supported]);

  const enable = async () => {
    setBusy(true); setMsg("");
    try {
      await subscribeToPush(navigator.platform + " · " + new Date().toISOString().slice(0, 10));
      setMsg("✓ Abone olundu. Uygulama kapalıyken de bildirim gelecek.");
      await refresh();
    } catch (e) {
      setMsg(`Açılamadı: ${e.message}`);
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true); setMsg("");
    try {
      await unsubscribeFromPush();
      setMsg("Abonelik iptal edildi.");
      await refresh();
    } catch (e) {
      setMsg(`Kapatılamadı: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const localTest = async () => {
    setMsg("");
    if (typeof Notification === "undefined") {
      setMsg("Bu tarayıcı bildirim desteklemiyor.");
      return;
    }
    try {
      let perm = Notification.permission;
      if (perm === "default") {
        perm = await Notification.requestPermission();
        setPermission(perm);
      }
      if (perm !== "granted") {
        setMsg("Bildirim izni verilmedi. iPhone'da: Ayarlar → Bildirimler → Kemal Namlı Çiftliği → Bildirimlere İzin Ver.");
        return;
      }
      // Show via service worker if available (works when app is backgrounded on iOS)
      const reg = await navigator.serviceWorker?.getRegistration?.("/");
      if (reg && reg.showNotification) {
        await reg.showNotification("Kemal Namlı Çiftliği", {
          body: "Yerel test bildirimi çalışıyor ✓",
          tag: "local-test",
          icon: "https://static.prod-images.emergentagent.com/jobs/fe9a795a-9b27-4bfd-9166-eea07adda0cf/images/de0f25fef2e7b6a99671adb5dfe2d7eadd23991c138ad11a61234e9051ce02e1.jpeg",
        });
      } else {
        new Notification("Kemal Namlı Çiftliği", { body: "Yerel test bildirimi çalışıyor ✓" });
      }
      setMsg("✓ Yerel bildirim tetiklendi. Ekranınızda görünmüyorsa iOS ayarlarını kontrol edin.");
    } catch (e) {
      setMsg(`Yerel test başarısız: ${e.message}`);
    }
  };

  const sendTest = async () => {
    if (!adminPin) { setMsg("Sunucu testi için önce PIN ile giriş yapın."); return; }
    setBusy(true); setMsg("");
    try {
      const res = await fetch(`${API}/push/test`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin: adminPin, title: "Kemal Namlı Çiftliği", body: testBody }),
      });
      if (!res.ok) throw new Error("PIN veya sunucu hatası");
      const d = await res.json();
      setMsg(`Gönderildi: ${d.sent} cihaz · Başarısız: ${d.failed}${d.detail ? " · " + d.detail : ""}`);
    } catch (e) {
      setMsg(`Test başarısız: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const permOk = permission === "granted";
  const permLabel = { granted: "İzin verildi", denied: "Reddedildi", default: "İzin sorulmadı", unavailable: "Yok" }[permission] || permission;

  return (
    <div className="push-setup">
      {ios && !standalone && (
        <div className="push-warn ios">
          <Smartphone size={18} />
          <div>
            <b>iPhone'da bildirim için önce yükle</b>
            <small>iOS 16.4+ gereklidir. Safari'de Paylaş → Ana Ekrana Ekle → simgeden aç → bu ekrana dön → "Bu cihazı abone et". Safari sekmesinde abonelik yapılamaz.</small>
          </div>
        </div>
      )}

      {/* Diagnostic panel */}
      <div className="push-diag" data-testid="push-diagnostics">
        <StatusRow ok={supported} label="Tarayıcı desteği" detail={supported ? "PushManager + ServiceWorker mevcut" : "Bu tarayıcıda desteklenmiyor"} />
        <StatusRow ok={swReady} label="Service Worker" detail={swReady ? "/sw.js aktif" : "Kayıt edilmedi (sayfayı yenileyin)"} />
        <StatusRow ok={permOk} label="Bildirim izni" detail={permLabel} />
        <StatusRow ok={subscribed} label="Bu cihaz abone" detail={subscribed ? "Sunucuya kayıtlı" : "Henüz abone değil"} />
        {ios && <StatusRow ok={standalone} label="Ana ekrandan açılmış" detail={standalone ? "PWA modu" : "Safari sekmesi (iOS'ta abone olamaz)"} />}
      </div>

      <div className="push-actions">
        {!subscribed ? (
          <button className="primary-btn" data-testid="push-enable" onClick={enable} disabled={busy || !supported}>
            {busy ? <Loader2 size={15} className="spin" /> : <BellRing size={15} />} Bu cihazı abone et
          </button>
        ) : (
          <button className="outline-btn" data-testid="push-disable" onClick={disable} disabled={busy}>
            {busy ? <Loader2 size={15} className="spin" /> : <BellOff size={15} />} Aboneliği iptal et
          </button>
        )}
        <button className="ghost-btn small" data-testid="push-local-test" onClick={localTest}>
          <Zap size={13} /> Yerel test (kolay tanı)
        </button>
      </div>

      <div className="push-test">
        <label className="form-field">
          <span>Sunucu test bildirim mesajı</span>
          <textarea data-testid="push-test-body" value={testBody} onChange={(e) => setTestBody(e.target.value)} rows={2} />
        </label>
        <button className="primary-btn" data-testid="push-test-send" onClick={sendTest} disabled={busy || !adminPin || count === 0}>
          <Send size={13} /> {count === 0 ? "Önce abone olun" : `Sunucudan test bildirimi gönder (${count} cihaz)`}
        </button>
      </div>

      {msg && (
        <div className={`push-msg ${msg.startsWith("✓") ? "ok" : msg.includes("başarısız") || msg.includes("Açılamadı") || msg.includes("verilmedi") ? "err" : ""}`} data-testid="push-msg">
          {msg.startsWith("✓") ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />} {msg}
        </div>
      )}

      <div className="push-info">
        <b>Nasıl çalışır?</b>
        <small>Sunucu her 15 dakikada uyarıları tarar. Acil (kırmızı) uyarılar — vadesi geçmiş çek, düşük stok, maaş günü, hasat zamanı — abone cihazlara otomatik push olarak gider. Aynı uyarı bir kez başarıyla gönderildikten sonra tekrar gönderilmez.</small>
      </div>
    </div>
  );
}
